/**
 * HOC version of mixins/Timing.js
 *
 * It provides `setTimer` and `clearTimer` utility methods. These work much like
 * the standard timeout, but offer some useful improvements:
 *
 *   - Any timer that is pending at unmount time will be automatically cleared.
 *
 *   - Timers can be created with a custom `id` for which only one timer can exist at
 *     a time; any pending timers with the same id will be automatically cleared.
 *
 *   - The delay parameter can be a number of milliseconds but can also be one of a
 *     few special named strings: "frame" and "immediate". This lets you set different
 *     kinds of timers (setTimeout/requestAnimationFrame/setImmediate) using a single
 *     method signature and behavior, and change a timer from one type to another.
 */

import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import getBaseClass from './getBaseClass'

let idInc = 0

const namedDelays = {
  frame: {
    setter: fn => requestAnimationFrame(fn),
    clearer: id => cancelAnimationFrame(id)
  },
  immediate: {
    setter: fn => setImmediate(fn),
    clearer: fn => clearImmediate(fn)
  }
}

// A few aliases for convenience/laziness:
namedDelays.animationFrame = namedDelays.raf = namedDelays.frame
namedDelays.tick = namedDelays.immediate

const Timing = WrappedComponent => {
  const BaseClass = getBaseClass(WrappedComponent)

  return class TimingWrapper extends BaseClass {
    static displayName = `Timing(${WrappedComponent.displayName ||
      'Component'})`

    static propTypes = {
      getRef: T.func // TODO remove and use regular refs when React addresses the issues at the end of this https://facebook.github.io/react/docs/higher-order-components.html
    }

    // onUnMount() {
    componentWillUnmount() {
      const clearers = this.$timerClearers
      if (clearers) {
        for (const id in clearers) {
          clearers[id]()
        }
        delete this.$timerClearers
      }
    }

    /**
     * Sets up a new timer.
     *
     * @param {Function} fn - The function to execute after the delay
     * @param {Number|String} delay - How long to delay. This can either be a number of milliseconds to
     *        wait, or one of the following special strings:
     *          * "frame" to wait until the next animation frame (requestAnimationFrame)
     *          * "immediate" to wait until the next yield of the execution thread (setImmediate/nextTick)
     * @param {String} [id] - An optional identifier for this timer. If `setTimer` is called with the
     *        same id more than once, the previous timer will automatically be canceled first.
     *
     * @returns {String} - The identifier for this timer. Will match the `id` param if specified; otherwise
     *          it will be an automatically generated id. This can be passed to `clearTimer` later.
     */
    setTimer = (fn, delay, id) => {
      if (id) {
        this.clearTimer(id)
      } else {
        id = '$id' + idInc++
      }
      const clearers =
        this.$timerClearers || (this.$timerClearers = Object.create(null))
      const fnWrap = function(...args) {
        delete clearers[id]
        fn.call(this, ...args)
      }
      const nativeId = _.isNumber(delay)
        ? setTimeout(fnWrap, delay)
        : namedDelays[delay].setter(fnWrap)
      clearers[id] = (_.isNumber(delay)
        ? clearTimeout
        : namedDelays[delay].clearer).bind(null, nativeId)
      return id
    }

    /**
     * Cancels an existing timer.
     *
     * @param {String} id - The identifier for the timer to be canceled, that was returned from `setTimer`.
     */
    clearTimer = id => {
      const clearers = this.$timerClearers
      if (clearers && clearers[id]) {
        clearers[id]()
        delete clearers[id]
      }
    }

    render() {
      const refProps = {}
      refProps[
        WrappedComponent.propTypes && WrappedComponent.propTypes.getRef
          ? 'getRef'
          : 'ref'
      ] = this.props.getRef
      return (
        <WrappedComponent
          {...this.props}
          {...refProps}
          setTimer={this.setTimer}
          clearTimer={this.clearTimer}
        />
      )
    }
  }

  // return hoistMethods(TimingWrapper, WrappedComponent, c => {
  //   return c.wrappedChild
  // })
}

export default Timing
